/**
 * Game action definitions & handlers
 * All state changes go through dispatch(action) only
 * Headless - no GUI
 */

import type { ActionHandler } from '@app-games/core';
import type { GameState, ActiveBoost, GoldenEvent, ConquestCountryState } from './state.js';
import { createInitialState } from './state.js';
import { PRODUCERS, getProducerCost } from './data/producers.js';
import { CLICK_UPGRADES, SPECIAL_UPGRADES } from './data/upgrades.js';
import { PRESTIGE_UPGRADES, getPrestigeUpgradeCost, PRESTIGE2_UPGRADES } from './data/prestige.js';
import { getTotalCps, getTapPower, getComboTimeout } from './systems/production.js';
import { calculateGcEarned, canPrestige, calculateCoresEarned, canPrestige2, getStartingIp } from './systems/prestige.js';
import { checkAchievements } from './data/achievements.js';
import { generateDailyMissions, getUtcDateString, getMissionTemplate, ALL_COMPLETE_BONUS_DOCS } from './data/missions.js';
import { COUNTRIES, getFacilityCost } from './data/conquest.js';
import { isCountryUnlocked, getInfiltrationPower } from './systems/conquest.js';
import { getNextPromotableRank } from './data/ranks.js';
import type { PrestigeUpgradeId, Prestige2UpgradeId } from './state.js';

// Action types
export type GameAction =
  | { type: 'TAP' }
  | { type: 'TICK'; payload: { deltaMs: number } }
  | { type: 'BUY_PRODUCER'; payload: { producerId: string; count?: number } }
  | { type: 'BUY_CLICK_UPGRADE'; payload: { index: number } }
  | { type: 'BUY_SPECIAL_UPGRADE'; payload: { upgradeId: string } }
  | { type: 'PRESTIGE_1' }
  | { type: 'PRESTIGE_2' }
  | { type: 'BUY_PRESTIGE_UPGRADE'; payload: { upgradeId: PrestigeUpgradeId } }
  | { type: 'BUY_PRESTIGE2_UPGRADE'; payload: { upgradeId: Prestige2UpgradeId } }
  | { type: 'CLAIM_OFFLINE_REWARD'; payload: { doubled: boolean } }
  | { type: 'TOUCH_GOLDEN_EVENT' }
  | { type: 'SPAWN_GOLDEN_EVENT' }
  | { type: 'CHECK_ACHIEVEMENTS' }
  | { type: 'AD_WATCHED'; payload: { rewardType: string } }
  | { type: 'USE_CLASSIFIED_DOC'; payload: { itemId: string } }
  | { type: 'ACTIVATE_BOOST'; payload: { boost: ActiveBoost } }
  | { type: 'CLEAN_EXPIRED_BOOSTS' }
  | { type: 'ADD_IP'; payload: { amount: number } }
  | { type: 'ADD_DOCS'; payload: { amount: number } }
  | { type: 'REFRESH_DAILY_MISSIONS' }
  | { type: 'UPDATE_MISSION_PROGRESS'; payload: { missionType: string; amount: number } }
  | { type: 'CLAIM_DAILY_MISSION'; payload: { index: number } }
  | { type: 'CLAIM_ALL_MISSIONS_BONUS' }
  | { type: 'SET_ACTIVE_COUNTRY'; payload: { countryId: string | null } }
  | { type: 'INVEST_CONQUEST'; payload: { countryId: string; amount: number } }
  | { type: 'BUILD_FACILITY'; payload: { countryId: string; facilityIdx: number } }
  | { type: 'UPGRADE_FACILITY'; payload: { countryId: string; facilityIdx: number } }
  | { type: 'UNLOCK_ENDING' }
  | { type: 'PROMOTE_RANK' }
  | { type: 'RESET' };

// TAP handler
const handleTap: ActionHandler<GameState, Extract<GameAction, { type: 'TAP' }>> = (state) => {
  const now = Date.now();
  const tapPower = getTapPower(state);
  const comboTimeout = getComboTimeout(state);

  // Update combo
  let newCombo = state.comboCount;
  if (now - state.comboLastTapAt <= comboTimeout) {
    newCombo += 1;
  } else {
    newCombo = 1;
  }
  const newMaxCombo = Math.max(state.maxCombo, newCombo);

  return {
    ...state,
    ip: state.ip + tapPower,
    totalIpEarned: state.totalIpEarned + tapPower,
    totalIpThisRun: state.totalIpThisRun + tapPower,
    tapCount: state.tapCount + 1,
    comboCount: newCombo,
    comboLastTapAt: now,
    maxCombo: newMaxCombo,
  };
};

// TICK handler -- auto-production
const handleTick: ActionHandler<GameState, Extract<GameAction, { type: 'TICK' }>> = (state, action) => {
  const { deltaMs } = action.payload;
  const cps = getTotalCps(state);
  const ipEarned = cps * (deltaMs / 1000);
  if (ipEarned <= 0 && state.activeBoosts.length === 0) return null;

  const now = Date.now();

  // Auto-click from prestige upgrade
  const autoClickLv = state.prestigeUpgrades.autoClick;
  let autoTapIp = 0;
  if (autoClickLv > 0) {
    const tapsPerSec = autoClickLv * 3;
    const tapPower = getTapPower({ ...state, comboCount: 0 }); // no combo for auto
    autoTapIp = tapPower * tapsPerSec * (deltaMs / 1000);
  }

  const totalEarned = ipEarned + autoTapIp;

  // Combo decay
  const comboTimeout = getComboTimeout(state);
  let newCombo = state.comboCount;
  if (state.comboCount > 0 && now - state.comboLastTapAt > comboTimeout) {
    newCombo = 0;
  }

  // Clean expired boosts
  const activeBoosts = state.activeBoosts.filter(b => now < b.endsAt);

  // Update play time
  const newPlayTime = state.totalPlayTimeMs + deltaMs;

  // Infiltration progress (active country only)
  let newConquest = state.conquest;
  const activeId = state.conquest.activeCountry;
  if (activeId) {
    const country = COUNTRIES.find(c => c.id === activeId);
    const cs = state.conquest.countries[activeId];
    if (country && cs && !cs.conquered) {
      const infPower = getInfiltrationPower(state);
      const progress = infPower * (deltaMs / 1000);
      if (progress > 0) {
        const newInvested = Math.min(cs.invested + progress, country.conquestCost);
        const newConquered = newInvested >= country.conquestCost;
        const newCountries = {
          ...state.conquest.countries,
          [activeId]: { ...cs, invested: newInvested, conquered: newConquered },
        };

        // quickInfiltration: on conquest, seed next country with bonus
        if (newConquered) {
          const qiLv = state.prestigeUpgrades?.quickInfiltration ?? 0;
          if (qiLv > 0) {
            const nextCountry = COUNTRIES.find(c => c.order === country.order + 1);
            if (nextCountry) {
              const bonusPct = qiLv * 0.1; // 10% per level
              const bonus = nextCountry.conquestCost * bonusPct;
              const existing = newCountries[nextCountry.id];
              if (!existing || !existing.conquered) {
                newCountries[nextCountry.id] = {
                  invested: (existing?.invested ?? 0) + bonus,
                  conquered: false,
                  facilities: existing?.facilities ?? [0, 0, 0],
                };
              }
            }
          }
        }

        newConquest = {
          ...state.conquest,
          countries: newCountries,
          activeCountry: newConquered ? null : activeId,
        };
      }
    }
  }

  return {
    ...state,
    ip: state.ip + totalEarned,
    totalIpEarned: state.totalIpEarned + totalEarned,
    totalIpThisRun: state.totalIpThisRun + totalEarned,
    comboCount: newCombo,
    activeBoosts,
    totalPlayTimeMs: newPlayTime,
    lastOnlineAt: now,
    conquest: newConquest,
  };
};

// BUY_PRODUCER handler
const handleBuyProducer: ActionHandler<GameState, Extract<GameAction, { type: 'BUY_PRODUCER' }>> = (state, action) => {
  const { producerId, count: buyCount = 1 } = action.payload;
  const def = PRODUCERS.find(p => p.id === producerId);
  if (!def) return null;

  const owned = state.producers[producerId]?.count ?? 0;
  const discountPct = state.prestigeUpgrades.agentDiscount * 10;

  // Calculate total cost for buying `buyCount` units
  let totalCost = 0;
  for (let i = 0; i < buyCount; i++) {
    totalCost += getProducerCost(def.baseCost, owned + i, discountPct);
  }

  if (state.ip < totalCost) return null;

  return {
    ...state,
    ip: state.ip - totalCost,
    producers: {
      ...state.producers,
      [producerId]: { count: owned + buyCount },
    },
  };
};

// BUY_CLICK_UPGRADE handler
const handleBuyClickUpgrade: ActionHandler<GameState, Extract<GameAction, { type: 'BUY_CLICK_UPGRADE' }>> = (state, action) => {
  const { index } = action.payload;
  const upg = CLICK_UPGRADES[index];
  if (!upg) return null;
  if (state.clickUpgradesBought.includes(index)) return null;
  // Must buy previous upgrade first (index 0 is always unlocked)
  if (index > 0 && !state.clickUpgradesBought.includes(index - 1)) return null;
  if (state.ip < upg.cost) return null;

  return {
    ...state,
    ip: state.ip - upg.cost,
    clickUpgradesBought: [...state.clickUpgradesBought, index],
  };
};

// BUY_SPECIAL_UPGRADE handler
const handleBuySpecialUpgrade: ActionHandler<GameState, Extract<GameAction, { type: 'BUY_SPECIAL_UPGRADE' }>> = (state, action) => {
  const { upgradeId } = action.payload;
  const upg = SPECIAL_UPGRADES.find(s => s.id === upgradeId);
  if (!upg) return null;
  if (state.specialUpgradesBought.includes(upgradeId)) return null;
  if (state.ip < upg.cost) return null;

  // Check condition
  const cond = upg.condition;
  if (cond.type === 'producers') {
    for (const req of cond.requirements) {
      if ((state.producers[req.producerId]?.count ?? 0) < req.count) return null;
    }
  } else if (cond.type === 'combo') {
    if (state.maxCombo < cond.maxCombo) return null;
  } else if (cond.type === 'playtime') {
    if (state.totalPlayTimeMs < cond.hours * 3600_000) return null;
  } else if (cond.type === 'totalProducers') {
    const total = Object.values(state.producers).reduce((s, p) => s + p.count, 0);
    if (total < cond.count) return null;
  }

  return {
    ...state,
    ip: state.ip - upg.cost,
    specialUpgradesBought: [...state.specialUpgradesBought, upgradeId],
  };
};

// PRESTIGE_1 handler
const handlePrestige1: ActionHandler<GameState, Extract<GameAction, { type: 'PRESTIGE_1' }>> = (state) => {
  if (!canPrestige(state)) return null;

  let gcEarned = calculateGcEarned(state.totalIpThisRun, state);
  if (state.prestigeBoostActive) gcEarned *= 2;
  const startingIp = getStartingIp(state);

  // quickStart: auto-buy first 5 producers (1 each)
  const producers: typeof state.producers = {};
  if (state.prestigeUpgrades.quickStart > 0) {
    for (let i = 0; i < 5 && i < PRODUCERS.length; i++) {
      producers[PRODUCERS[i].id] = { count: 1 };
    }
  }

  return {
    ...state,
    // Reset
    ip: startingIp,
    totalIpThisRun: 0,
    producers,
    clickUpgradesBought: [],
    specialUpgradesBought: [],
    activeBoosts: [],
    prestigeBoostActive: false,
    comboCount: 0,
    comboLastTapAt: 0,
    baseTapPower: 1,
    currentGoldenEvent: null,
    conquest: { activeCountry: null, countries: {}, endingUnlocked: false },
    rank: 'E',
    // Keep
    totalIpEarned: state.totalIpEarned,
    gc: state.gc + gcEarned,
    totalGcEarned: state.totalGcEarned + gcEarned,
    prestigeCount: state.prestigeCount + 1,
    // Preserve prestige upgrades, achievements, stats, docs, p2, etc.
  };
};

// PRESTIGE_2 handler
const handlePrestige2: ActionHandler<GameState, Extract<GameAction, { type: 'PRESTIGE_2' }>> = (state) => {
  if (!canPrestige2(state)) return null;

  const coresEarned = calculateCoresEarned(state.totalGcEarned, state);

  // Deep reset - reset GC, prestige upgrades, everything from prestige 1
  const fresh = createInitialState();

  return {
    ...fresh,
    // Preserved across prestige 2
    cores: state.cores + coresEarned,
    totalCoresEarned: state.totalCoresEarned + coresEarned,
    prestige2Count: state.prestige2Count + 1,
    prestige2Upgrades: state.prestige2Upgrades,
    achievements: state.achievements,
    totalPlayTimeMs: state.totalPlayTimeMs,
    totalAdsWatched: state.totalAdsWatched,
    classifiedDocs: state.classifiedDocs,
    totalDocsUsed: state.totalDocsUsed,
    totalIpEarned: state.totalIpEarned,
    gameStartedAt: state.gameStartedAt,
    sessionStartAt: Date.now(),
    bgmVolume: state.bgmVolume,
    sfxVolume: state.sfxVolume,
  };
};

// BUY_PRESTIGE_UPGRADE handler
const handleBuyPrestigeUpgrade: ActionHandler<GameState, Extract<GameAction, { type: 'BUY_PRESTIGE_UPGRADE' }>> = (state, action) => {
  const { upgradeId } = action.payload;
  const def = PRESTIGE_UPGRADES.find(u => u.id === upgradeId);
  if (!def) return null;

  const currentLv = state.prestigeUpgrades[upgradeId];
  if (currentLv >= def.maxLevel) return null;

  const cost = getPrestigeUpgradeCost(def, currentLv);
  if (state.gc < cost) return null;

  return {
    ...state,
    gc: state.gc - cost,
    prestigeUpgrades: {
      ...state.prestigeUpgrades,
      [upgradeId]: currentLv + 1,
    },
  };
};

// BUY_PRESTIGE2_UPGRADE handler
const handleBuyPrestige2Upgrade: ActionHandler<GameState, Extract<GameAction, { type: 'BUY_PRESTIGE2_UPGRADE' }>> = (state, action) => {
  const { upgradeId } = action.payload;
  const def = PRESTIGE2_UPGRADES.find(u => u.id === upgradeId);
  if (!def) return null;

  const currentLv = state.prestige2Upgrades[upgradeId];
  if (currentLv >= def.maxLevel) return null;
  if (state.cores < def.cost) return null;

  return {
    ...state,
    cores: state.cores - def.cost,
    prestige2Upgrades: {
      ...state.prestige2Upgrades,
      [upgradeId]: currentLv + 1,
    },
  };
};

// CLAIM_OFFLINE_REWARD handler
const handleClaimOfflineReward: ActionHandler<GameState, Extract<GameAction, { type: 'CLAIM_OFFLINE_REWARD' }>> = (state, action) => {
  const amount = action.payload.doubled ? state.offlineIpPending * 2 : state.offlineIpPending;
  if (amount <= 0) return null;

  return {
    ...state,
    ip: state.ip + amount,
    totalIpEarned: state.totalIpEarned + amount,
    totalIpThisRun: state.totalIpThisRun + amount,
    offlineIpPending: 0,
  };
};

// TOUCH_GOLDEN_EVENT handler
const handleTouchGoldenEvent: ActionHandler<GameState, Extract<GameAction, { type: 'TOUCH_GOLDEN_EVENT' }>> = (state) => {
  const evt = state.currentGoldenEvent;
  if (!evt) return null;
  if (Date.now() > evt.expiresAt) return null;

  const cps = getTotalCps(state);
  let ipGain = 0;
  let docsGain = 0;
  let newBoosts = [...state.activeBoosts];
  const now = Date.now();

  switch (evt.type) {
    case 'intel_leak':
      ipGain = cps * 3;
      break;
    case 'agent_frenzy':
      newBoosts.push({ type: 'tap5x', multiplier: 5, endsAt: now + 10_000 });
      break;
    case 'money_launder':
      ipGain = state.ip * 0.05;
      break;
    case 'facility_boost':
      newBoosts.push({ type: 'cps3x', multiplier: 3, endsAt: now + 15_000 });
      break;
    case 'supply_drop':
      docsGain = 1;
      break;
  }

  // Golden luck bonus
  const goldenLuckLv = state.prestigeUpgrades.goldenLuck;
  if (goldenLuckLv > 0 && ipGain > 0) {
    ipGain = Math.floor(ipGain * (1 + goldenLuckLv * 0.2));
  }

  // Achievement bonus
  const achEventRewardPct = state.achievements['C03']?.unlocked ? 10 : 0;
  if (achEventRewardPct > 0 && ipGain > 0) {
    ipGain = Math.floor(ipGain * (1 + achEventRewardPct / 100));
  }

  // Schedule next golden event
  let baseInterval = 300_000 + Math.random() * 600_000; // 5-15 min
  // Orbital surveillance: -50%
  if (state.specialUpgradesBought.includes('orbitalSurveillance')) {
    baseInterval *= 0.5;
  }
  // Achievement: S05 -20% frequency
  if (state.achievements['S05']?.unlocked) baseInterval *= 0.8;
  // Achievement: C04 -10%
  if (state.achievements['C04']?.unlocked) baseInterval *= 0.9;

  return {
    ...state,
    ip: state.ip + ipGain,
    totalIpEarned: state.totalIpEarned + ipGain,
    totalIpThisRun: state.totalIpThisRun + ipGain,
    classifiedDocs: state.classifiedDocs + docsGain,
    activeBoosts: newBoosts,
    currentGoldenEvent: null,
    goldenEventsTouched: state.goldenEventsTouched + 1,
    lastGoldenEventAt: now,
    nextGoldenEventAt: now + baseInterval,
  };
};

// SPAWN_GOLDEN_EVENT handler
const handleSpawnGoldenEvent: ActionHandler<GameState, Extract<GameAction, { type: 'SPAWN_GOLDEN_EVENT' }>> = (state) => {
  const now = Date.now();
  if (state.currentGoldenEvent && now < state.currentGoldenEvent.expiresAt) return null;
  if (now < state.nextGoldenEventAt) return null;

  // Randomly pick event type
  const rand = Math.random();
  let eventType: GoldenEvent['type'];
  if (rand < 0.30) eventType = 'intel_leak';
  else if (rand < 0.55) eventType = 'agent_frenzy';
  else if (rand < 0.80) eventType = 'money_launder';
  else if (rand < 0.95) eventType = 'facility_boost';
  else eventType = 'supply_drop';

  const evt: GoldenEvent = {
    id: `ge-${now}`,
    type: eventType,
    appearedAt: now,
    expiresAt: now + 7000, // 7 seconds
  };

  return {
    ...state,
    currentGoldenEvent: evt,
  };
};

// CHECK_ACHIEVEMENTS handler
const handleCheckAchievements: ActionHandler<GameState, Extract<GameAction, { type: 'CHECK_ACHIEVEMENTS' }>> = (state) => {
  const newlyUnlocked = checkAchievements(state);
  if (newlyUnlocked.length === 0) return null;

  const newAchievements = { ...state.achievements };
  const now = Date.now();
  for (const id of newlyUnlocked) {
    newAchievements[id] = { unlocked: true, unlockedAt: now };
  }

  return {
    ...state,
    achievements: newAchievements,
  };
};

// AD_WATCHED handler
const handleAdWatched: ActionHandler<GameState, Extract<GameAction, { type: 'AD_WATCHED' }>> = (state) => {
  return {
    ...state,
    totalAdsWatched: state.totalAdsWatched + 1,
  };
};

// USE_CLASSIFIED_DOC handler
const handleUseDoc: ActionHandler<GameState, Extract<GameAction, { type: 'USE_CLASSIFIED_DOC' }>> = (state, action) => {
  const { itemId } = action.payload;
  const now = Date.now();
  const cps = getTotalCps(state);

  let cost = 0;
  let ipGain = 0;
  let newBoosts = [...state.activeBoosts];

  switch (itemId) {
    case 'ip_boost_30s': cost = 3; newBoosts.push({ type: 'cps2x', multiplier: 2, endsAt: now + 30_000 }); break;
    case 'mega_tap_30s': cost = 3; newBoosts.push({ type: 'tap3x', multiplier: 3, endsAt: now + 30_000 }); break;
    case 'auto_click_30s': cost = 3; newBoosts.push({ type: 'autoClick', multiplier: 10, endsAt: now + 30_000 }); break;
    case 'instant_golden': cost = 5; break; // handled in UI
    case 'timeskip_30s': cost = 5; ipGain = cps * 7200; break;
    case 'prestige_boost':
      if (state.prestigeBoostActive) return null; // already purchased, block re-buy
      cost = 8;
      break;
    default: return null;
  }

  if (state.classifiedDocs < cost) return null;

  return {
    ...state,
    classifiedDocs: state.classifiedDocs - cost,
    totalDocsUsed: state.totalDocsUsed + cost,
    ip: state.ip + ipGain,
    totalIpEarned: state.totalIpEarned + ipGain,
    totalIpThisRun: state.totalIpThisRun + ipGain,
    activeBoosts: newBoosts,
    prestigeBoostActive: itemId === 'prestige_boost' ? true : state.prestigeBoostActive,
  };
};

// ACTIVATE_BOOST handler
const handleActivateBoost: ActionHandler<GameState, Extract<GameAction, { type: 'ACTIVATE_BOOST' }>> = (state, action) => {
  return {
    ...state,
    activeBoosts: [...state.activeBoosts, action.payload.boost],
  };
};

// CLEAN_EXPIRED_BOOSTS handler
const handleCleanBoosts: ActionHandler<GameState, Extract<GameAction, { type: 'CLEAN_EXPIRED_BOOSTS' }>> = (state) => {
  const now = Date.now();
  const filtered = state.activeBoosts.filter(b => now < b.endsAt);
  if (filtered.length === state.activeBoosts.length) return null;
  return { ...state, activeBoosts: filtered };
};

// ADD_IP handler (for testing/debug)
const handleAddIp: ActionHandler<GameState, Extract<GameAction, { type: 'ADD_IP' }>> = (state, action) => {
  return {
    ...state,
    ip: state.ip + action.payload.amount,
    totalIpEarned: state.totalIpEarned + action.payload.amount,
    totalIpThisRun: state.totalIpThisRun + action.payload.amount,
  };
};

// ADD_DOCS handler
const handleAddDocs: ActionHandler<GameState, Extract<GameAction, { type: 'ADD_DOCS' }>> = (state, action) => {
  return { ...state, classifiedDocs: state.classifiedDocs + action.payload.amount };
};

// REFRESH_DAILY_MISSIONS handler - checks date and regenerates if needed
const handleRefreshDailyMissions: ActionHandler<GameState, Extract<GameAction, { type: 'REFRESH_DAILY_MISSIONS' }>> = (state) => {
  const today = getUtcDateString();
  if (state.dailyMissionDate === today && state.dailyMissions.length > 0) return null;

  const missions = generateDailyMissions(today);
  return {
    ...state,
    dailyMissions: missions,
    dailyMissionDate: today,
    dailyMissionAllClaimed: false,
  };
};

// UPDATE_MISSION_PROGRESS handler - updates progress for matching missions
const handleUpdateMissionProgress: ActionHandler<GameState, Extract<GameAction, { type: 'UPDATE_MISSION_PROGRESS' }>> = (state, action) => {
  const { missionType, amount } = action.payload;
  if (state.dailyMissions.length === 0) return null;

  let changed = false;
  const newMissions = state.dailyMissions.map(m => {
    if (m.claimed) return m;
    const template = getMissionTemplate(m.templateId);
    if (!template || template.type !== missionType) return m;
    if (m.progress >= m.target) return m;

    changed = true;
    return { ...m, progress: Math.min(m.progress + amount, m.target) };
  });

  if (!changed) return null;
  return { ...state, dailyMissions: newMissions };
};

// CLAIM_DAILY_MISSION handler
const handleClaimDailyMission: ActionHandler<GameState, Extract<GameAction, { type: 'CLAIM_DAILY_MISSION' }>> = (state, action) => {
  const { index } = action.payload;
  const mission = state.dailyMissions[index];
  if (!mission || mission.claimed || mission.progress < mission.target) return null;

  const template = getMissionTemplate(mission.templateId);
  if (!template) return null;

  const newMissions = [...state.dailyMissions];
  newMissions[index] = { ...mission, claimed: true };

  let newBoosts = [...state.activeBoosts];
  const now = Date.now();
  if (template.reward.boostSeconds && template.reward.boostType) {
    newBoosts.push({
      type: template.reward.boostType as ActiveBoost['type'],
      multiplier: template.reward.boostType === 'cps2x' ? 2 : template.reward.boostType === 'cps3x' ? 3 : 5,
      endsAt: now + template.reward.boostSeconds * 1000,
    });
  }

  return {
    ...state,
    dailyMissions: newMissions,
    classifiedDocs: state.classifiedDocs + template.reward.docs,
    activeBoosts: newBoosts,
  };
};

// CLAIM_ALL_MISSIONS_BONUS handler - bonus for completing all daily missions
const handleClaimAllMissionsBonus: ActionHandler<GameState, Extract<GameAction, { type: 'CLAIM_ALL_MISSIONS_BONUS' }>> = (state) => {
  if (state.dailyMissionAllClaimed) return null;
  const allClaimed = state.dailyMissions.length > 0 && state.dailyMissions.every(m => m.claimed);
  if (!allClaimed) return null;

  return {
    ...state,
    classifiedDocs: state.classifiedDocs + ALL_COMPLETE_BONUS_DOCS,
    dailyMissionAllClaimed: true,
  };
};

// SET_ACTIVE_COUNTRY handler
const handleSetActiveCountry: ActionHandler<GameState, Extract<GameAction, { type: 'SET_ACTIVE_COUNTRY' }>> = (state, action) => {
  const { countryId } = action.payload;
  if (countryId !== null) {
    if (!isCountryUnlocked(countryId, state)) return null;
    const cs = state.conquest.countries[countryId];
    if (cs?.conquered) return null;
  }
  // Ensure country entry exists
  let newCountries = state.conquest.countries;
  if (countryId && !newCountries[countryId]) {
    newCountries = {
      ...newCountries,
      [countryId]: { invested: 0, conquered: false, facilities: [0, 0, 0] },
    };
  }
  return {
    ...state,
    conquest: { ...state.conquest, activeCountry: countryId, countries: newCountries },
  };
};

// INVEST_CONQUEST handler
const handleInvestConquest: ActionHandler<GameState, Extract<GameAction, { type: 'INVEST_CONQUEST' }>> = (state, action) => {
  const { countryId, amount } = action.payload;
  if (amount <= 0 || state.ip < amount) return null;
  const country = COUNTRIES.find(c => c.id === countryId);
  if (!country) return null;
  if (!isCountryUnlocked(countryId, state)) return null;

  const cs: ConquestCountryState = state.conquest.countries[countryId] || { invested: 0, conquered: false, facilities: [0, 0, 0] };
  if (cs.conquered) return null;

  const newInvested = Math.min(cs.invested + amount, country.conquestCost);
  const newConquered = newInvested >= country.conquestCost;

  return {
    ...state,
    ip: state.ip - amount,
    conquest: {
      ...state.conquest,
      countries: {
        ...state.conquest.countries,
        [countryId]: { ...cs, invested: newInvested, conquered: newConquered },
      },
    },
  };
};

// BUILD_FACILITY handler
const handleBuildFacility: ActionHandler<GameState, Extract<GameAction, { type: 'BUILD_FACILITY' }>> = (state, action) => {
  const { countryId, facilityIdx } = action.payload;
  const country = COUNTRIES.find(c => c.id === countryId);
  if (!country) return null;
  const cs = state.conquest.countries[countryId];
  if (!cs?.conquered) return null;
  const fac = country.facilities[facilityIdx];
  if (!fac) return null;
  if (cs.facilities[facilityIdx] > 0) return null;
  if (state.ip < fac.baseCost) return null;

  const newFacilities: [number, number, number] = [...cs.facilities] as [number, number, number];
  newFacilities[facilityIdx] = 1;

  return {
    ...state,
    ip: state.ip - fac.baseCost,
    conquest: {
      ...state.conquest,
      countries: {
        ...state.conquest.countries,
        [countryId]: { ...cs, facilities: newFacilities },
      },
    },
  };
};

// UPGRADE_FACILITY handler
const handleUpgradeFacility: ActionHandler<GameState, Extract<GameAction, { type: 'UPGRADE_FACILITY' }>> = (state, action) => {
  const { countryId, facilityIdx } = action.payload;
  const country = COUNTRIES.find(c => c.id === countryId);
  if (!country) return null;
  const cs = state.conquest.countries[countryId];
  if (!cs?.conquered) return null;
  const fac = country.facilities[facilityIdx];
  if (!fac) return null;
  const currentLv = cs.facilities[facilityIdx];
  if (currentLv < 1) return null;

  const cost = getFacilityCost(fac.baseCost, currentLv);
  if (state.ip < cost) return null;

  const newFacilities: [number, number, number] = [...cs.facilities] as [number, number, number];
  newFacilities[facilityIdx] = currentLv + 1;

  return {
    ...state,
    ip: state.ip - cost,
    conquest: {
      ...state.conquest,
      countries: {
        ...state.conquest.countries,
        [countryId]: { ...cs, facilities: newFacilities },
      },
    },
  };
};

// UNLOCK_ENDING handler
const handleUnlockEnding: ActionHandler<GameState, Extract<GameAction, { type: 'UNLOCK_ENDING' }>> = (state) => {
  if (state.conquest.endingUnlocked) return null;
  return {
    ...state,
    conquest: { ...state.conquest, endingUnlocked: true },
  };
};

// PROMOTE_RANK handler
const handlePromoteRank: ActionHandler<GameState, Extract<GameAction, { type: 'PROMOTE_RANK' }>> = (state) => {
  const next = getNextPromotableRank(state.rank ?? 'E', state.totalIpEarned, state.prestigeCount);
  if (!next) return null;
  if (state.ip < next.promotionCost) return null;
  return {
    ...state,
    ip: state.ip - next.promotionCost,
    rank: next.grade,
  };
};

// RESET handler
const handleReset: ActionHandler<GameState, Extract<GameAction, { type: 'RESET' }>> = () => {
  return createInitialState();
};

/** All action handlers map */
export const actionHandlers = {
  TAP: handleTap,
  TICK: handleTick,
  BUY_PRODUCER: handleBuyProducer,
  BUY_CLICK_UPGRADE: handleBuyClickUpgrade,
  BUY_SPECIAL_UPGRADE: handleBuySpecialUpgrade,
  PRESTIGE_1: handlePrestige1,
  PRESTIGE_2: handlePrestige2,
  BUY_PRESTIGE_UPGRADE: handleBuyPrestigeUpgrade,
  BUY_PRESTIGE2_UPGRADE: handleBuyPrestige2Upgrade,
  CLAIM_OFFLINE_REWARD: handleClaimOfflineReward,
  TOUCH_GOLDEN_EVENT: handleTouchGoldenEvent,
  SPAWN_GOLDEN_EVENT: handleSpawnGoldenEvent,
  CHECK_ACHIEVEMENTS: handleCheckAchievements,
  AD_WATCHED: handleAdWatched,
  USE_CLASSIFIED_DOC: handleUseDoc,
  ACTIVATE_BOOST: handleActivateBoost,
  CLEAN_EXPIRED_BOOSTS: handleCleanBoosts,
  ADD_IP: handleAddIp,
  ADD_DOCS: handleAddDocs,
  REFRESH_DAILY_MISSIONS: handleRefreshDailyMissions,
  UPDATE_MISSION_PROGRESS: handleUpdateMissionProgress,
  CLAIM_DAILY_MISSION: handleClaimDailyMission,
  CLAIM_ALL_MISSIONS_BONUS: handleClaimAllMissionsBonus,
  SET_ACTIVE_COUNTRY: handleSetActiveCountry,
  INVEST_CONQUEST: handleInvestConquest,
  BUILD_FACILITY: handleBuildFacility,
  UPGRADE_FACILITY: handleUpgradeFacility,
  UNLOCK_ENDING: handleUnlockEnding,
  PROMOTE_RANK: handlePromoteRank,
  RESET: handleReset,
} as const;

// Re-export utility functions for UI
export { getTotalCps, getTapPower, getComboTimeout, getCritChance, getCritMultiplier } from './systems/production.js';
export { calculateGcEarned, canPrestige, calculateCoresEarned, canPrestige2, getStartingIp, getOfflineCapHours, getOfflineEfficiency } from './systems/prestige.js';
export { calculateOfflineIp } from './systems/offline.js';
export { getProducerCost } from './data/producers.js';
export { COUNTRIES, getFacilityCost } from './data/conquest.js';
export { isCountryUnlocked, isAllConquered, getConqueredCount, getConquestCpsMult, getConquestTapMult, getInfiltrationPower } from './systems/conquest.js';
